import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Alert } from '../ui/alert';
import { Badge } from '../ui/badge';
import { CheckCircle, Clock } from 'lucide-react';
import { cleanRepeatedText, cn } from '../../lib/utils';
import type { DocumentMobileTab } from '../../lib/documentHelpers';
import DocumentMobileTabs from './DocumentMobileTabs';
import DocumentMetadataPanel from './DocumentMetadataPanel';
import DocumentPdfViewer from './DocumentPdfViewer';
import DocumentActionBar from './DocumentActionBar';

interface DocumentPageLayoutProps {
  isDesktop: boolean;
  isAppSidebarOpen: boolean;
  activeTab: DocumentMobileTab;
  onTabChange: (tab: DocumentMobileTab) => void;
  documentInfo: any | null;
  signerId: string;
  authLoading: boolean;
  hasQueryParams: boolean;
  pdfBlob: Blob | null;
  pdfUrl: string | null;
  isLoadingPdf: boolean;
  pageError: string | null;
  onLoadDocument: () => void;
  showSignWithSima: boolean;
  showRelease: boolean;
  isUpdatingRegistryStatus: boolean;
  onSignWithSima: () => void;
  onRelease: () => void;
  onReject: () => void;
}

const DETAILS_PANEL_MIN_WIDTH = 280;
const DETAILS_PANEL_MAX_WIDTH = 640;
const PDF_PANEL_MIN_WIDTH = 320;

const DocumentPageLayout: React.FC<DocumentPageLayoutProps> = ({
  isDesktop,
  isAppSidebarOpen,
  activeTab,
  onTabChange,
  documentInfo,
  signerId,
  authLoading,
  hasQueryParams,
  pdfBlob,
  pdfUrl,
  isLoadingPdf,
  pageError,
  onLoadDocument,
  showSignWithSima,
  showRelease,
  isUpdatingRegistryStatus,
  onSignWithSima,
  onRelease,
  onReject,
}) => {
  const { t } = useLanguage();
  const hasSignerId = signerId.trim().length > 0;
  const showActions = Boolean(documentInfo && !documentInfo.status && (showSignWithSima || showRelease));
  const [mobileActionsExpanded, setMobileActionsExpanded] = useState(true);
  const desktopLayoutRef = useRef<HTMLDivElement>(null);
  const [detailsPanelWidth, setDetailsPanelWidth] = useState(() => (isAppSidebarOpen ? 400 : 460));
  const title = documentInfo ? cleanRepeatedText(documentInfo.title) || t('document') : t('document');

  const mobileActionButtonCount = useMemo(() => {
    let count = 1;
    if (showSignWithSima) count += 1;
    if (showRelease) count += 1;
    return count;
  }, [showRelease, showSignWithSima]);

  const mobileActionBarPadding = useMemo(() => {
    if (!showActions) return undefined;
    if (!mobileActionsExpanded) return 'pb-20';
    if (mobileActionButtonCount <= 1) return 'pb-36';
    if (mobileActionButtonCount === 2) return 'pb-44';
    return 'pb-52';
  }, [mobileActionButtonCount, mobileActionsExpanded, showActions]);

  useEffect(() => {
    setMobileActionsExpanded(activeTab === 'details');
  }, [activeTab, documentInfo?.docGuid]);

  const handleDetailsResizeStart = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const container = desktopLayoutRef.current;
    if (!container) return;

    const onPointerMove = (moveEvent: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const maxWidth = Math.min(
        DETAILS_PANEL_MAX_WIDTH,
        rect.width - PDF_PANEL_MIN_WIDTH,
      );
      const nextWidth = Math.min(
        maxWidth,
        Math.max(DETAILS_PANEL_MIN_WIDTH, moveEvent.clientX - rect.left),
      );
      setDetailsPanelWidth(nextWidth);
    };

    const onPointerUp = () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
  }, []);

  const handleTabChange = (tab: DocumentMobileTab) => {
    onTabChange(tab);
    if (tab === 'document' && !pdfBlob && !isLoadingPdf && documentInfo) {
      onLoadDocument();
    }
  };

  const metadataPanel = (
    <DocumentMetadataPanel
      documentInfo={documentInfo}
      signerId={signerId}
      authLoading={authLoading}
      hasQueryParams={hasQueryParams}
      compact={!isDesktop}
      isMobile={!isDesktop}
      pdfUrl={pdfUrl}
      isPdfLoading={isLoadingPdf}
    />
  );

  const pdfViewer = (
    <DocumentPdfViewer
      pdfBlob={pdfBlob}
      pdfUrl={pdfUrl}
      isLoading={isLoadingPdf}
      hasDocumentInfo={!!documentInfo}
      fileName={documentInfo?.title}
      pageError={isDesktop ? pageError : activeTab === 'document' ? pageError : null}
      onLoadDocument={onLoadDocument}
      scrollAreaClassName={!isDesktop ? mobileActionBarPadding : undefined}
    />
  );

  const actionBar = showActions ? (
    <DocumentActionBar
      showSignWithSima={showSignWithSima}
      showRelease={showRelease}
      isUpdatingRegistryStatus={isUpdatingRegistryStatus}
      hasSignerId={hasSignerId}
      onSignWithSima={onSignWithSima}
      onRelease={onRelease}
      onReject={onReject}
      variant={isDesktop ? 'desktop' : 'mobile'}
      expanded={mobileActionsExpanded}
      onExpandedChange={setMobileActionsExpanded}
    />
  ) : null;

  const mobileDetailsHeader = documentInfo ? (
    <div className="space-y-2 border-b pb-3">
      <div className="flex items-start justify-between gap-2">
        <h1 className="line-clamp-2 text-base font-semibold leading-snug">{title}</h1>
        <Badge variant={documentInfo.status ? 'success' : 'warning'} className="shrink-0">
          {documentInfo.status ? (
            <>
              <CheckCircle className="h-3 w-3" aria-hidden />
              {t('documentStatusDone')}
            </>
          ) : (
            <>
              <Clock className="h-3 w-3" aria-hidden />
              {t('documentStatusInProgress')}
            </>
          )}
        </Badge>
      </div>
      {(documentInfo.organisationName || documentInfo.organisationTIN) && (
        <p className="truncate text-sm text-muted-foreground">
          {documentInfo.organisationTIN && `${documentInfo.organisationTIN} - `}
          {documentInfo.organisationName || t('notAvailable')}
        </p>
      )}
    </div>
  ) : null;

  if (isDesktop) {
    return (
      <div ref={desktopLayoutRef} className="flex min-h-0 flex-1 overflow-hidden md:flex-row">
        <div
          style={{ width: detailsPanelWidth }}
          className="flex h-full min-h-0 min-w-0 shrink-0 flex-col border-r bg-background"
        >
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden scroll-smooth p-4 [scrollbar-gutter:stable]">
            {metadataPanel}
          </div>
          {actionBar}
        </div>
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t('documentDetailsResize')}
          aria-valuemin={DETAILS_PANEL_MIN_WIDTH}
          aria-valuemax={DETAILS_PANEL_MAX_WIDTH}
          aria-valuenow={detailsPanelWidth}
          onPointerDown={handleDetailsResizeStart}
          className="group relative z-10 w-1 shrink-0 touch-none"
        >
          <div className="absolute inset-y-0 -left-1.5 -right-1.5 cursor-col-resize" />
          <div className="mx-auto h-full w-px bg-border transition-colors group-hover:bg-primary/40 group-active:bg-primary/60" />
        </div>
        <div className="min-h-0 min-w-0 flex-1 overflow-hidden bg-muted/30 p-4">
          {pdfViewer}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="shrink-0 px-4 py-2">
        <DocumentMobileTabs activeTab={activeTab} onTabChange={handleTabChange} />
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        {activeTab === 'details' ? (
          <div
            className={cn(
              'h-full overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-3',
              mobileActionBarPadding,
            )}
          >
            <div className="space-y-3">
              {mobileDetailsHeader}
              {pageError && (
                <Alert variant="destructive">
                  {pageError}
                </Alert>
              )}
              {metadataPanel}
            </div>
          </div>
        ) : (
          <div className="h-full overflow-hidden bg-muted/30 px-2 py-2">{pdfViewer}</div>
        )}
      </div>

      {actionBar}
    </div>
  );
};

export default DocumentPageLayout;
